/**
 * UPT-2079 L3 (§4.3) — checksum d'un Module Spec, EXTRAIT de `data/fixtureRepository.ts` (UPT-2095) : sérialisation
 * canonique à clés triées (`canonicalJson`, comme `ORDER_MAP_ENTRIES_BY_KEYS` côté serveur), SHA-256 hex minuscule par
 * `crypto.subtle.digest` (`checksumOf`), repli FNV-1a 64 bits ×4 (`fnv1a64Hex`, NON cryptographique, documenté) quand
 * `crypto.subtle` manque (jest). Le dépôt de démonstration importe ces fonctions (aucune duplication) ;
 * `moduleSpecChecksum(spec)` est le raccourci du brouillon.
 */

/** Sérialisation canonique (clés triées à tous les niveaux). */
export const canonicalJson = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value ?? null);
};

const toHex = (bytes: Uint8Array): string =>
  Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

/** Octets UTF-8 sur un ArrayBuffer propre (forme attendue par `crypto.subtle.digest`). */
const utf8Encode = (text: string): Uint8Array<ArrayBuffer> => {
  const encoded = new TextEncoder().encode(text);
  const copy = new Uint8Array(new ArrayBuffer(encoded.byteLength));
  copy.set(encoded);
  return copy;
};

/**
 * Empreinte FNV-1a 64 bits (hex), NON cryptographique : repli de démonstration seulement, quand `crypto.subtle`
 * manque (jest). Quatre passes chaînées pour occuper les 64 caractères d'un SHA-256.
 */
export const fnv1a64Hex = (bytes: Uint8Array): string => {
  const prime = BigInt('0x100000001b3');
  const mask = BigInt('0xffffffffffffffff');
  let seed = BigInt('0xcbf29ce484222325');
  let hex = '';
  for (let pass = 0; pass < 4; pass += 1) {
    let hash = seed;
    for (const byte of bytes) {
      hash ^= BigInt(byte);
      hash = (hash * prime) & mask;
    }
    hex += hash.toString(16).padStart(16, '0');
    seed = hash ^ BigInt(pass + 1);
  }
  return hex;
};

/** SHA-256 hex minuscule par `crypto.subtle` (asynchrone) ; repli FNV-1a documenté sans `crypto.subtle`. */
export const checksumOf = async (normalized: string): Promise<string> => {
  const bytes = utf8Encode(normalized);
  const subtle = typeof crypto !== 'undefined' ? crypto.subtle : undefined;
  if (subtle && typeof subtle.digest === 'function') {
    try {
      const digest = await subtle.digest('SHA-256', bytes);
      return toHex(new Uint8Array(digest));
    } catch {
      // Contexte non sécurisé : repli ci-dessous.
    }
  }
  return fnv1a64Hex(bytes);
};

/** Checksum d'un document Module Spec (sérialisation canonique puis `checksumOf`). */
export const moduleSpecChecksum = (spec: unknown): Promise<string> => checksumOf(canonicalJson(spec));
